import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  Calendar, 
  Award, 
  TrendingUp, 
  TrendingDown,
  BarChart3,
  CheckCircle,
  XCircle,
  AlertCircle,
  Filter,
  Download,
  Eye,
  FileText,
  Target
} from 'lucide-react';
import { formatDate } from '../../utils/format';
import api from '../api';
import { toast } from 'react-toastify';

const EtudiantNotesTab = ({ etudiant, etudiantId }) => {
  const [notesData, setNotesData] = useState(null);
  const [notesControle, setNotesControle] = useState([]);
  const [notesExamen, setNotesExamen] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [selectedAnnee, setSelectedAnnee] = useState('');
  const [activeSubTab, setActiveSubTab] = useState('controles');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnnees();
  }, []);

  useEffect(() => {
    if (selectedAnnee) {
      fetchNotesData();
    }
  }, [selectedAnnee]);

  const fetchAnnees = async () => {
    try {
      const response = await api.get('/etablissement/annees-academiques/');
      setAnnees(response.data);
      // Sélectionner l'année active par défaut
      const activeYear = response.data.find(annee => annee.status);
      if (activeYear) {
        setSelectedAnnee(activeYear.id);
      } else if (response.data.length > 0) {
        setSelectedAnnee(response.data[0].id);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des années académiques');
    }
  };

  const fetchNotesData = async () => {
    if (!selectedAnnee) return;
    
    setLoading(true);
    try {
      // Récupérer les notes pour l'année sélectionnée
      const [controlesResponse, examensResponse, statistiquesResponse] = await Promise.all([
        api.get(`/etudiant/notes-controle/${etudiantId}/`, { params: { annee: selectedAnnee } }),
        api.get(`/etudiant/notes-examen/${etudiantId}/`, { params: { annee: selectedAnnee } }),
        api.get(`/etudiant/statistiques-notes/${etudiantId}/`, { params: { annee: selectedAnnee } })
      ]);

      setNotesControle(controlesResponse.data);
      setNotesExamen(examensResponse.data);
      setNotesData(statistiquesResponse.data);
      setError(null);
    } catch (error) {
      setError('Erreur lors du chargement des données de notes');
      toast.error('Erreur lors du chargement des données de notes');
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = 'blue', subtitle = null }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
          {subtitle && (
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <Icon className={`h-12 w-12 text-${color}-600 opacity-20`} />
      </div>
    </div>
  );

  const getMention = (note) => {
    if (note >= 16) return { label: 'Excellent', color: 'text-green-600' };
    if (note >= 14) return { label: 'Très bien', color: 'text-blue-600' };
    if (note >= 12) return { label: 'Bien', color: 'text-yellow-600' };
    if (note >= 10) return { label: 'Assez bien', color: 'text-orange-600' };
    if (note >= 8) return { label: 'Passable', color: 'text-red-400' };
    return { label: 'Insuffisant', color: 'text-red-600' };
  };

  const MentionBadge = ({ note }) => {
    const mention = getMention(note);
    return (
      <span className={`text-xs font-medium ${mention.color}`}>
        {mention.label}
      </span>
    );
  };

  const subTabs = [
    { id: 'controles', label: 'Contrôles', icon: FileText },
    { id: 'examens', label: 'Examens', icon: Award },
    { id: 'statistiques', label: 'Statistiques', icon: BarChart3 }
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
        <span className="ml-3 text-gray-600">Chargement des notes...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-2" />
        <div className="text-red-600">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtre par année */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Notes & Évaluations</h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <Filter className="h-5 w-5 text-gray-400 mr-2" />
            <select
              value={selectedAnnee}
              onChange={(e) => setSelectedAnnee(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent"
            >
              <option value="">Sélectionner une année</option>
              {annees.map(annee => (
                <option key={annee.id} value={annee.id}>
                  {annee.libelle} {annee.status && '(Actuelle)'}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Statistiques générales */}
      {notesData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Moyenne générale"
            value={`${notesData.moyenne_generale}/20`}
            icon={Target}
            color="blue"
            subtitle={`${notesData.total_notes} notes`}
          />
          <StatCard
            title="Meilleure note"
            value={`${notesData.meilleure_note}/20`}
            icon={TrendingUp}
            color="green"
            subtitle={notesData.matiere_meilleure_note}
          />
          <StatCard
            title="Note la plus faible"
            value={`${notesData.note_plus_faible}/20`}
            icon={TrendingDown}
            color="red"
            subtitle={notesData.matiere_note_plus_faible}
          />
          <StatCard
            title="Rang en classe"
            value={`${notesData.rang}/${notesData.total_etudiants}`}
            icon={Award}
            color="purple"
            subtitle="Position"
          />
        </div>
      )}

      {/* Sous-onglets */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-1 p-4">
            {subTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveSubTab(tab.id)}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    activeSubTab === tab.id
                      ? 'bg-[#202654] text-white'
                      : 'text-gray-600 hover:text-[#202654] hover:bg-gray-50'
                  }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeSubTab === 'controles' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes de Contrôle</h3>
              {notesControle.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aucune note de contrôle pour cette année</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Matière
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Note
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Mention
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Observation
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {notesControle.map((note) => (
                        <tr key={note.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {note.controle.module.module.libelle}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {note.controle.libelle}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(note.controle.date_controle)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {note.note}/20
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <MentionBadge note={note.note} />
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                            {note.observation || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeSubTab === 'examens' && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Notes d'Examen</h3>
              {notesExamen.length === 0 ? (
                <div className="text-center py-8">
                  <Award className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">Aucune note d'examen pour cette année</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Matière
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Session
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Note
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Mention
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Observation
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {notesExamen.map((note) => (
                        <tr key={note.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {note.session.module.module.libelle}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {note.session.examen.libelle}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {formatDate(note.session.date_examen)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {note.absent ? 'Absent' : `${note.note}/20`}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {!note.absent && <MentionBadge note={note.note} />}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              note.absent ? 'bg-red-100 text-red-800' :
                              note.triche ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {note.absent ? 'Absent' : note.triche ? 'Fraude' : 'Présent'}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                            {note.observation || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeSubTab === 'statistiques' && notesData && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Statistiques détaillées</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Répartition par matière */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="text-md font-medium text-gray-900 mb-4">Moyennes par matière</h4>
                  <div className="space-y-3">
                    {notesData.moyennes_par_matiere && notesData.moyennes_par_matiere.map((matiere, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">{matiere.nom}</span>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-900">{matiere.moyenne}/20</span>
                          <MentionBadge note={matiere.moyenne} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Évolution temporelle */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h4 className="text-md font-medium text-gray-900 mb-4">Évolution</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Progression</span>
                      <span className={`text-sm font-medium ${
                        notesData.evolution_moyenne >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {notesData.evolution_moyenne >= 0 ? '+' : ''}{notesData.evolution_moyenne}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Nombre d'évaluations</span>
                      <span className="text-sm font-medium text-gray-900">{notesData.total_notes}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Taux de réussite</span>
                      <span className="text-sm font-medium text-green-600">{notesData.taux_reussite}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EtudiantNotesTab; 